import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Alert, Button, Skeleton, Tag } from 'antd'
import { z } from 'zod'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { EnterpriseSupplementEditor } from './enterprise-supplement-modal.tsx'

export const enterpriseBatchSearchSchema = z.object({
  ids: z.preprocess(
    (value) => (Array.isArray(value) ? value.join(',') : value),
    z
      .string()
      .regex(/^\d+(,\d+){0,19}$/, '请选择 1 至 20 个服务对象')
      .transform((value) => value.split(',').map(Number))
      .refine(
        (ids) => ids.every((id) => Number.isSafeInteger(id) && id > 0) && new Set(ids).size === ids.length,
        '服务对象 ID 无效或重复'
      )
  )
})

export function EnterpriseBatchPage({ ids }: { ids: number[] }) {
  const trpc = useTRPC()
  const queries = useQueries({
    queries: ids.map((subjectId) => trpc.order.subjects.detail.queryOptions({ subjectId }))
  })
  const [activeId, setActiveId] = useState(ids[0])
  const [visited, setVisited] = useState(() => new Set([ids[0]]))
  const [busy, setBusy] = useState(false)
  return (
    <div className="bg-page flex h-dvh min-h-0 flex-col overflow-hidden">
      <WorkspaceHeader>
        <div>
          <h1 className="m-0 text-base font-semibold">批量补录资料</h1>
          <span className="text-content-muted text-xs">{ids.length} 个服务对象 · 逐个保存</span>
        </div>
      </WorkspaceHeader>
      <main className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 overflow-hidden p-3 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-1">
        <nav aria-label="服务对象列表" className="flex max-h-56 min-w-0 flex-col gap-3 overflow-auto lg:max-h-none">
          {ids.map((id, index) => {
            const item = queries[index].data
            const complete =
              item?.documentStatus.company &&
              item.documentStatus.businessLicense &&
              item.documentStatus.identity &&
              item.documentStatus.identityOriginal &&
              item.documentStatus.relationship === 'consistent'
            return (
              <button
                key={id}
                type="button"
                disabled={busy}
                aria-current={id === activeId ? 'true' : undefined}
                onClick={() => {
                  setActiveId(id)
                  setVisited((previous) => new Set(previous).add(id))
                }}
                className={cn(
                  'bg-surface border-border flex w-full shrink-0 cursor-pointer flex-col gap-2 rounded-xl border p-4 text-left disabled:cursor-not-allowed disabled:opacity-60',
                  id === activeId && 'border-accent/50 bg-accent/10'
                )}
              >
                <span className="text-content-muted text-xs">
                  服务对象 {index + 1} · #{id}
                </span>
                <span className="text-sm font-semibold">{item?.name ?? '正在加载'}</span>
                <span className="text-content-muted text-xs">{item?.customerName}</span>
                {item && (
                  <Tag className="self-start" color={complete ? 'success' : 'warning'}>
                    {complete ? '资料齐全' : '待补录资料'}
                  </Tag>
                )}
                {queries[index].error && <span className="text-danger text-xs">加载失败</span>}
              </button>
            )
          })}
        </nav>
        <section className="bg-surface border-border min-h-0 min-w-0 overflow-hidden rounded-xl border">
          {[...visited].map((id) => {
            const query = queries[ids.indexOf(id)]
            const item = query.data
            return (
              <div
                key={id}
                hidden={id !== activeId}
                className={id === activeId ? 'flex h-full min-h-0 flex-col' : 'hidden'}
              >
                {query.isPending && <Skeleton active className="p-6" />}
                {query.error && (
                  <Alert
                    className="m-6"
                    type="error"
                    title={query.error.message}
                    action={<Button onClick={() => void query.refetch()}>重试</Button>}
                  />
                )}
                {item && (
                  <>
                    <header className="border-border shrink-0 border-b px-6 py-4">
                      <h2 className="m-0 text-base font-semibold">补充服务对象资料</h2>
                      <p className="text-content-muted mt-1 mb-0 text-xs">
                        {item.name} · {item.customerName}
                      </p>
                    </header>
                    <div className="min-h-0 flex-1 p-6">
                      {item.kind === 'person' || item.isArchived ? (
                        <Alert
                          type="info"
                          showIcon
                          title={
                            item.isArchived ? '服务对象已归档，请先恢复后补录资料' : '该对象类型不支持企业资料补录'
                          }
                        />
                      ) : (
                        <EnterpriseSupplementEditor initial={item} onBusyChange={setBusy} />
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </section>
      </main>
    </div>
  )
}
